<script setup lang="ts">
import type { UserCatalogDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import MaintenanceModeFields from '@/components/maintenanceMode/MaintenanceModeFields.vue';
import type { NotificationMessage } from '@/components/notifications/types';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import { useSystemStore } from '@/stores/systemStore.ts';

interface MaintenanceModeDialogProps {
  modelValue: boolean;
  loading?: boolean;
  exceptionNotification?: NotificationMessage;
  notificationMessage?: NotificationMessage;
  loggedInUsers?: UserCatalogDto[];
  // Validation state from parent
  isValid?: boolean;
  // Field name context for vee-validate
  fieldNameContext?: string;
}

interface MaintenanceModeDialogEmits {
  'update:modelValue': [value: boolean];
  submit: [];
  cancel: [];
}

const {
  loading = false,
  isValid = true,
  fieldNameContext = undefined,
  exceptionNotification = undefined,
  notificationMessage = undefined,
  loggedInUsers = undefined,
} = defineProps<MaintenanceModeDialogProps>();

const emit = defineEmits<MaintenanceModeDialogEmits>();

const systemStore = useSystemStore();

const { t } = useTranslation();

const getFieldName = (name: string) => {
  const prefix = fieldNameContext ? fieldNameContext + '.' : '';
  return prefix + name;
};

const passwordFieldName = computed(() => getFieldName('password'));
const ignoreLoggedInUsersFieldName = computed(() => getFieldName('ignoreLoggedInUsers'));

// Event handlers
function handleDialogClose(value: boolean) {
  if (loading && !value) {
    emit('update:modelValue', true);
    return;
  }

  emit('update:modelValue', value);
  if (!value) {
    emit('cancel');
  }
}

function handleSubmit() {
  emit('submit'); // parent's onSubmit will validate again
}

function handleCancel() {
  if (loading) return;
  emit('update:modelValue', false);
  emit('cancel');
}

function handleKeydownEnter() {
  if (isValid) {
    handleSubmit();
  }
}
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    size="medium"
    :hideClose="loading"
    @update:modelValue="handleDialogClose"
  >
    <template #title>
      {{ $t('SecurityCheck.Title') }}
    </template>

    <template #text>
      <template v-if="!systemStore.maintenanceModeInfo">
        <h4 class="on-off-white">{{ $t('UpdateUpgradeInformationStepMaintenanceHeader') }}</h4>
        <p>{{ $t('UpgradeInformationStepMaintenanceMsg') }}</p>
        <v-alert
          class="mt-5 mb-7"
          type="warning"
          :text="$t('UpdateUpgradeInformationStepMaintenanceWarning')"
          variant="tonal"
        />
      </template>

      <form @submit.prevent="handleSubmit">
        <p class="text-pre-line mb-5">
          {{ $t('SecurityCheck.Message') }}
        </p>
        <MaintenanceModeFields
          :passwordFieldName="passwordFieldName"
          :ignoreLoggedInUsersFieldName="ignoreLoggedInUsersFieldName"
          :exceptionNotification="exceptionNotification"
          :notificationMessage="notificationMessage"
          :loggedInUsers="loggedInUsers"
          :onPasswordEnter="handleKeydownEnter"
        />

        <!-- Action buttons -->
        <div class="d-flex justify-end mt-4">
          <Button
            variant="text"
            class="mr-5"
            :label="t('Cancel')"
            :disabled="loading"
            @click="handleCancel"
          />
          <VeeSubmitButton :text="t('SecurityCheck.Proceed')" :loading="loading" />
        </div>
      </form>
    </template>
  </Dialog>
</template>
